<template>
  <sticky-header-layout>
    <template #header>
      <div class="action-container">
        <div class="pagination-container">
          <el-pagination
            :disabled="isLoading"
            :current-page="listQuery.page"
            :page-sizes="listQuery.sizes"
            :page-size="listQuery.size"
            :total="listQuery.total"
            layout="total, sizes, pager, jumper"
            background
            size="mini"
            @size-change="handleSizeChange"
            @current-change="handlePageChange"
          />
        </div>
        <div class="button-container">
          <el-button
            :disabled="isLoading"
            type="primary"
            icon="el-icon-search"
            size="mini"
            @click="handleSearch"
          >{{ $t('button.search') }}</el-button>
        </div>
      </div>
      <div class="filter-container">
        <label-wrapper :label="$t('table.store_name')">
          <el-input
            v-model="storeQuery"
            :disabled="isLoading"
            @keyup.enter.native="handleSearch"
          />
        </label-wrapper>
      </div>
    </template>

    <el-table
      v-loading="isLoading"
      :data="list"
      stripe
      border
    >
      <el-table-column
        v-for="{ type, ...column } in columnList"
        :key="type"
        :prop="type"
        :label="column.title || $t(`table.${type}`)"
        v-bind="column.custom"
      >
        <template #default="{ row }">
          <template v-if="type === 'website'">
            <ul class="website-items">
              <template
                v-for="{ key, title } in websiteList"
              >
                <li
                  v-if="row[key] && isValidUrl(row[key])"
                  :key="key"
                >
                  <a
                    :href="row[key]"
                    target="_blank"
                  >{{ title }}</a>
                </li>
              </template>
            </ul>
          </template>

          <template v-if="type === 'approved' || type === 'status'">
            <template
              v-if="
                row.approved === null
                  || (type === 'status' && !row.approved)
              "
            >
              N/A
            </template>
            <template v-else-if="row[type]">
              <font-awesome-icon icon="check" />
            </template>
            <template v-else>
              <font-awesome-icon icon="times" />
            </template>
          </template>

          <template v-else>
            {{ row[type] }}
          </template>
        </template>
      </el-table-column>

      <el-table-column
        :label="$t('table.action')"
        fixed="right"
        align="center"
        width="60"
      >
        <template #default="{ row }">
          <edit-button
            @click="handleEditMerchantInfo(row)"
          />
        </template>
      </el-table-column>
    </el-table>

    <el-dialog
      v-loading="isFetchingMerchantDetail"
      :visible.sync="isEditMerchantDialogOpen"
      :close-on-click-modal="!isMerchantDetailDialogDisabled"
      :close-on-press-escape="!isMerchantDetailDialogDisabled"
      :before-close="closeEditMerchantDialog"
      :show-close="false"
      :title="dialog.data ? dialog.data.store_name : ''"
      width="50%"
      top="6vh"
    >
      <template v-if="dialog.data">
        <div class="merchant-detail-wrapper">
          <template v-for="{ key } in merchantDetailAttributes">
            <label-wrapper
              :key="key"
              :label="$t(`table.${key}`)"
              class="pt-2"
            >
              <!-- Phone -->
              <div
                v-if="key === 'phone'"
              >
                <p class="my-0">Office: {{ dialog.data.office_number || 'N/A' }}</p>
                <p class="my-0">Mobile: {{ dialog.data.mobile_number || 'N/A' }}</p>
              </div>
              <!-- Phone -->

              <!-- Address -->
              <div
                v-else-if="key === 'address'"
                v-html="getAddressHtml(dialog.data)"
              />
              <div
                v-else-if="key === 'payment_setting'"
              >
                <template v-if="hasDialogPaymentDetail">
                  <p class="my-0">Bank Name: {{ dialog.data.payment_setting.bank_name }}</p>
                  <p class="my-0">Account Type: {{ dialog.data.payment_setting.bank_account_type }}</p>
                  <p class="my-0">Account Holder Name: {{ dialog.data.payment_setting.bank_account_name }}</p>
                  <p class="my-0">Account Number: {{ dialog.data.payment_setting.bank_account_number }}</p>
                  <p class="my-0">Currency: {{ dialog.data.payment_setting.bank_account_currency }}</p>
                </template>
                <template v-else>
                  N/A
                </template>
              </div>
              <!-- Address -->

              <!-- Categories -->
              <div v-else-if="key === 'categories'">
                <template v-if="dialog.data[key].length === 0">
                  N/A
                </template>
                <template
                  v-for="option in dialog.data[key]"
                  v-else
                >
                  <el-tag
                    :key="option"
                    class="mr-1"
                  >{{ option }}</el-tag>
                </template>
              </div>
              <!-- Categories -->

              <!-- Shipment -->
              <div v-else-if="key === 'shipment_options'">
                <template v-if="dialog.data[key].length === 0">
                  N/A
                </template>
                <template
                  v-for="option in dialog.data[key]"
                  v-else
                >
                  <div :key="option.delivery_method">
                    <template v-if="option.delivery_method !== 'Store Location'">
                      <span>{{ option.delivery_method }}: </span>
                      <span>{{ option.delivery_time_from }} to {{ option.delivery_time_to }} day(s)</span>
                    </template>
                  </div>
                </template>
              </div>
              <!-- Shipment -->

              <!-- Pickup -->
              <div v-else-if="key === 'pickup_options'">
                <template v-if="dialog.data[key].length === 0">
                  N/A
                </template>
                <template
                  v-for="option in dialog.data[key]"
                  v-else
                >
                  <div :key="option.title">
                    <p class="my-0 text-decoration-underline">{{ option.title }}: </p>
                    <p class="my-0">{{ option.phone }}</p>
                    <p class="my-0">{{ option.address }}</p>
                  </div>
                </template>
              </div>
              <!-- Pickup -->

              <template v-else>
                {{ dialog.data[key] }}
              </template>
            </label-wrapper>
          </template>

          <template v-for="doc in dialog.data.documents">
            <label-wrapper
              :key="doc.type"
              :label="$t(`table.${doc.type}`)"
              class="pt-2"
            >
              <a
                :href="doc.url"
                target="_blank"
              >
                <template v-if="checkImageURL(doc.url)">
                  <img
                    :src="doc.url"
                    style="max-width: 200px;"
                  >
                </template>
                <template v-else>
                  <span class="text-decoration-underline">Document</span>
                </template>
              </a>
            </label-wrapper>
          </template>

          <label-wrapper
            key="store_coupon_image"
            label="Store Coupon Image"
            class="pt-2"
          >
            <el-upload
              v-loading="isUploading"
              v-model="dialog.data.store_coupon_image"
              :file-list="dialog.storeCouponImageList"
              :limit="1"
              :disabled="isUploading"
              :http-request="handleUpload"
              :before-upload="handleBeforeUpload"
              :on-exceed="handleUploaderlimitExceed"
              :on-error="uploadfailed"
              :on-success="uploadSuccessful"
              list-type="picture"
              drag
              accept=".png, .jpg"
              action="noneed"
              :data="{field_name:'store_coupon_image'}"
            >
              <i class="el-icon-upload" />
              <div class="el-upload__text">Drop Image here or <em>click to upload</em></div>
            </el-upload>
          </label-wrapper>

        </div>

        <label-wrapper
          :class="['pt-3 pl-3', { disabled: isMerchantDetailDialogDisabled }]"
          :label="$t('table.approval')"
        >
          <el-radio
            v-model="dialog.data.approved"
            :label="1"
            class="pt-2"
          >{{ $t('table.approve') }} </el-radio>
          <el-radio
            v-model="dialog.data.approved"
            :label="0"
            class="pt-2"
          >{{ $t('table.reject') }} </el-radio>
        </label-wrapper>

        <label-wrapper
          v-if="dialog.data && dialog.approved === null && dialog.data.approved === 0"
          :label="`${$t('table.reject_reason')} (${$t('common.optional')})`"
          :class="['pt-3 pl-3', { disabled: isMerchantDetailDialogDisabled }]"
        >
          <el-input
            v-model="dialog.data.reject_reason"
            type="textarea"
          />
        </label-wrapper>

        <label-wrapper
          v-if="dialog.data && dialog.approved === 0"
          :label="$t('table.reject_reason')"
          class="pt-1 pl-3"
        >
          <span>{{ dialog.data.reject_reason }}</span>
        </label-wrapper>

        <label-wrapper
          v-if="dialog.data"
          :label="$t('table.status')"
          :class="['pt-3 pl-3', { disabled: isMerchantDetailDialogDisabled }]"
        >
          <el-radio
            v-model="dialog.data.status"
            :label="1"
            class="pt-2"
          >{{ $t('table.online') }}</el-radio>
          <el-radio
            v-model="dialog.data.status"
            :label="0"
            class="pt-2"
          >{{ $t('table.offline') }}</el-radio>
        </label-wrapper>

        <span slot="footer">
          <el-button
            :disabled="isMerchantDetailDialogDisabled"
            @click="closeEditMerchantDialog"
          >{{ $t('button.cancel') }}</el-button>
          <el-button
            :disabled="isMerchantDetailDialogDisabled"
            type="danger"
            @click="handleConfirm"
          >{{ $t('button.confirm') }}</el-button>
        </span>
      </template>
    </el-dialog>

  </sticky-header-layout>
</template>

<script>
import LabelWrapper from '../../components/form/LabelWrapper.vue'
import EditButton from '../../components/buttons/EditButton.vue'

import useTablePaging from '../../mixins/useTablePaging'
import MarketplaceService from '../../api/marketplace'
import { checkImageURL } from '../../utils/validate'

import {
  columnList,
  websiteList,
  merchantDetailAttributes,
} from '../../const/marketplace/merchants'

export default {
  name: 'MarketplaceMerchants',
  components: {
    EditButton,
    LabelWrapper,
  },
  mixins: [useTablePaging],
  data() {
    return {
      isLoading: false,
      isUploading: false,
      isFetchingMerchantDetail: false,
      isEditMerchantDialogOpen: false,

      columnList,
      websiteList,
      merchantDetailAttributes,

      storeQuery: null,
      dialog: {
        storeCouponImageList: [],
        data: null,
        approved: null,
      },
    }
  },
  computed: {
    isMerchantDetailDialogDisabled() {
      return this.isFetchingMerchantDetail || this.isLoading
    },
    hasDialogPaymentDetail() {
      return this.dialog.data
        && (
          this.dialog.data.payment_setting.bank_account_currency
          || this.dialog.data.payment_setting.bank_account_name
          || this.dialog.data.payment_setting.bank_account_number
          || this.dialog.data.payment_setting.bank_account_type
          || this.dialog.data.payment_setting.bank_name
        )
    },
  },
  mounted() {
    this.handleSearch()
  },
  methods: {
    isValidUrl(url) {
      const regex = /^(?:http(s)?:\/\/)/
      return regex.test(url)
    },
    getPayload() {
      return {
        keywords: this.storeQuery,
        page: this.storeQuery && this.storeQuery.length > 0 ? 1 : this.listQuery.page,
        size: this.listQuery.size,
      }
    },
    async getList() {
      this.isLoading = true
      const payload = this.getPayload()
      try {
        const response = await MarketplaceService.getMerchantsInfo(payload)
        const { data: newList = [], pagination } = response
        if (Array.isArray(newList)) {
          this.list = newList
        }

        this.list.forEach((row) => {
          row.store_email = row.store_email.join(', ')
          // Handle website URL
          websiteList.forEach(({ key }) => {
            if (!row[key]) return

            if (!this.isValidUrl(row[key])) {
              if (row[key].match(/www.*/)) {
                row[key] = `https://${row[key]}`
              }
            }
            if (
              key === 'facebook_url'
              && (!row[key].includes('facebook') || row[key] === 'https://www.facebook.com/')
            ) {
              row[key] = ''
            }
            if (
              key === 'instagram_url'
              && (!row[key].includes('instagram') || row[key] === 'https://www.instagram.com/')
            ) {
              row[key] = ''
            }
          })
        })
        this.listQuery.total = parseInt(pagination.total_items, 0)
      } catch (error) {
        console.log(error);
        this.list = []
      } finally {
        this.isLoading = false
      }
    },
    async getMerchantDetail(id) {
      this.isFetchingMerchantDetail = true
      try {
        const { data } = await MarketplaceService.getMerchantDetail(id)
        data.store_email = data.store_email.join(', ')
        this.dialog.data = data
        if (this.dialog.data.store_coupon_image) {
          this.dialog.storeCouponImageList.push({
            name: '',
            url: this.dialog.data.store_coupon_image,
          })
        } 
      } finally {
        this.isFetchingMerchantDetail = false
      }
    },
    async updateMerchantInfo(merchant, store_coupon_image) {
      this.isLoading = true
      try {
        await MarketplaceService.updateMerchantInfo(merchant, store_coupon_image)
      } finally {
        this.updateMerchantData(merchant)
        this.isLoading = false
        this.closeEditMerchantDialog()
      }
    },
    handleSearch() {
      this.getList()
    },
    handleEditMerchantInfo(rowData) {
      this.openEditMerchantDialog(rowData)
    },
    openEditMerchantDialog(merchant) {
      this.getMerchantDetail(merchant.id)
      this.dialog.data = null
      this.dialog.storeCouponImageList = []
      this.dialog.approved = merchant.approved
      this.isEditMerchantDialogOpen = true
    },
    closeEditMerchantDialog() {
      this.isEditMerchantDialogOpen = false
    },
    handleConfirm() {
      this.updateMerchantInfo(this.dialog.data, this.dialog.data.store_coupon_image)
    },
    updateMerchantData(merchant) {
      const index = this.list.findIndex(row => row.id === merchant.id)
      this.list[index] = merchant
    },
    getAddressHtml({ address1, address2, city }) {
      return [address1, address2, city].filter(ele => !!ele).join('<br>') || 'N/A'
    },
    checkImageURL,
    async handleUpload(uploadAction) {
      this.isUploading = true

      // try {
        this.dialog.data[uploadAction.data.field_name] = uploadAction.file
        uploadAction.onSuccess()
      // } catch (error) {
        // uploadAction.onError(error)
      // }
    },
    uploadSuccessful() {
      // this.$refs.uploader.clearFiles()
      this.isUploading = false
    },
    uploadfailed(error) {
      console.log(error)
      this.isUploading = false
      this.$alert(
        'Please try again later.',
        'Upload Failed', {
          type: 'error',
          confirmButtonText: 'OK',
        },
      )
    },
    handleBeforeUpload(file) {
      if (!file.name.includes('.png') && !file.name.includes('.jpg') && !file.name.includes('.jpeg')) {
        this.$alert(
          'Please double check you are uploading a .png, .jpg, or .jpeg file',
          'Incorrect File Type', {
            type: 'error',
            confirmButtonText: 'OK',
          },
        )
        return false
      }

      return true
    },
    handleUploaderlimitExceed() {
      this.$alert(
        'Sorry, please upload one file at a time!',
        'Too Many Files', {
          type: 'error',
          confirmButtonText: 'OK',
        },
      )
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/view-base.scss";
@import "~@/assets/styles/table.scss";

.filter-container {
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  flex-grow: 1;
  .filter-title {
    font-size: 13px;
    margin-right: 5px;
  }
}

.website-items {
  list-style: none;
  padding-left: 0;
}

.merchant-detail-wrapper {
  display: flex;
  flex-wrap: wrap;

  > * {
    flex: 1;
    flex-basis: calc(50% - 32px);
    max-width: calc(50% - 32px);
    border-bottom: 1px solid #e7e7e7;
    margin: 1px 16px;
    padding-bottom: 10px;
  }
}

::v-deep {
  .el-table__row {
    .cell {
      a {
        text-decoration: underline;
      }
    }
  }

  .el-upload-list__item-thumbnail {
    width: auto;
    height: 100%;
  }
}

</style>

